import { Link, useLocation } from "react-router-dom";
import type { CourseTreeNode } from "@/types/generated";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { useCourseBasePath } from "@/hooks/useCourseBasePath";
import { useHideCourseProgress } from "@/hooks/useHideCourseProgress";
import { StatusIcon } from "@/components/ui/status-icon";
import { ProgressRing } from "@/components/ui/progress-ring";
import {
  buildIntroUnveilQueryString,
  buildOutroContentEntryQueryString,
  resolveFirstSubIntroChapterId,
  resolveLastSubOutroChapterId,
  stripCourseNavQueryString,
} from "@/lib/chapterNavigation";
import { stripThirdLevelNumbering } from "@/lib/courseUtils";
import { LessonStatus } from "@/components/course/tree/types";
import { treeTitleClass } from "@/components/course/tree/treeStyles";
import { cn } from "@/lib/utils";

interface SubChapterNodeProps {
  subchapter: CourseTreeNode;
}

/**
 * TOC lesson link — first subchapters of chapters with intro route into the
 * stacked chapter-intro reveal path; last subchapters of chapters with outro
 * route into the stacked outro content-entry path; other lessons open plain content.
 */
const SubChapterNode = ({ subchapter }: SubChapterNodeProps) => {
  const { selectedNodeId, focusedNodeId, progressSubchapters, nodeIndex } =
    useCourseDetailStore();
  const lessonProgress = progressSubchapters[subchapter.id];
  const basePath = useCourseBasePath();
  const location = useLocation();
  const hideProgress = useHideCourseProgress();
  const isSelected =
    selectedNodeId === subchapter.id || focusedNodeId === subchapter.id;
  const status = lessonProgress?.status ?? LessonStatus.NotStarted;
  const progressPercent = lessonProgress?.progressPercent ?? 0;

  const introChapterId = resolveFirstSubIntroChapterId(
    subchapter.id,
    nodeIndex
  );
  const outroChapterId = introChapterId
    ? null
    : resolveLastSubOutroChapterId(subchapter.id, nodeIndex);

  const targetId = introChapterId ?? outroChapterId ?? subchapter.id;
  let search = stripCourseNavQueryString(location.search);
  if (introChapterId) {
    search = buildIntroUnveilQueryString(location.search);
  } else if (outroChapterId) {
    search = buildOutroContentEntryQueryString(location.search);
  }
  const targetPath = `${basePath}/${targetId}${search}`;

  return (
    <Link
      to={targetPath}
      id={`node-${subchapter.id}`}
      className={`relative block pl-3 pr-4 py-2 transition-colors ${
        isSelected ? "bg-accent" : ""
      }`}
      title={subchapter.title}
    >
      {/* Active indicator line */}
      {isSelected && (
        <span className="absolute -left-px top-1 bottom-1 w-0.75 rounded-r bg-primary" />
      )}

      <div className="flex items-center gap-2">
        {!hideProgress &&
          (status === LessonStatus.InProgress ? (
            <ProgressRing
              percentage={progressPercent}
              size="xs"
              fillColor="var(--secondary)"
            />
          ) : (
            <StatusIcon status={status} />
          ))}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <div
            className={cn(
              "text-[14px] leading-snug",
              isSelected && "font-bold",
              treeTitleClass(isSelected)
            )}
          >
            {stripThirdLevelNumbering(subchapter.title)}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default SubChapterNode;
